// 아크(검기 호): 반달 모양으로 쓸어 지나가는 두꺼운 호 메시. UV 스크롤 노이즈로 결을 흘리고, 진행에 따라 디졸브로 사라진다.
// 쓰는 칸: length = 호 반경(m), width = 가운데 두께(m), sweep·start = 쓸어 지나가는 각, roll = 평면 기울기(0 바닥과 평행, PI/2 세로),
//          ahead = 호 중심을 앞으로 옮기는 거리, life·color·color2·intensity·seed. 드로우콜 1.
// Blender 메시로 바꿀 때의 규약은 vfx-library/README.md의 "Blender로 바꿀 자리" 절을 따른다(같은 UV: u = 쓸어 지나가는 방향 0..1, v = 안쪽 0 → 바깥쪽 1).
import * as THREE from 'three/webgpu';
import { abs, clamp, float, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { clamp01, easeOut, planeFrame } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const ARC_SEG = 24;
/** 끝이 뾰족한 반달: 가운데가 가장 두껍다 */
const TAPER = new Float32Array(ARC_SEG + 1);
for (let i = 0; i <= ARC_SEG; i++) TAPER[i] = Math.pow(Math.max(0, Math.sin((Math.PI * i) / ARC_SEG)), 0.65);

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z, head = ctl.w;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const body = band.oneMinus().pow(1.3);
  const core = band.oneMinus().pow(3);
  // UV 스크롤: 결이 호를 따라 흐른다
  const n = texture(noise, vec2(u.mul(2.4).sub(age.mul(t2.w)), v.mul(0.8).add(seed))).r;
  // 그려지는 앞머리와 지워지는 꼬리
  const reveal = float(1).sub(smoothstep(head.sub(0.05), head, u));
  const lead = smoothstep(head.sub(0.35), head, u).mul(reveal);
  const tailPos = clamp(age.sub(0.35).mul(1.9), 0, 1.4);
  const fadeTail = smoothstep(tailPos.sub(0.25), tailPos, u);
  // 디졸브: 시간이 갈수록 노이즈가 높은 곳만 남다가 사라진다
  const thr = smoothstep(0.3, 1.0, age);
  const keep = smoothstep(thr, thr.add(0.18), n.mul(0.8).add(body.mul(0.35)));
  const alpha = reveal.mul(fadeTail).mul(keep).mul(env);
  const mixK = clamp(core.mul(0.55).add(lead.mul(0.45)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, mixK).mul(t1.w).mul(0.55).mul(body.mul(0.7).add(0.5)).mul(n.mul(0.5).add(0.75));

  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = alpha;
  return m;
}

export class ArcModule extends BatchModule {
  readonly kind: ModuleKind = 'arc';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.arc) {
    super(cap, stripLayout(ARC_SEG), makeMaterial(noise), 21, 'fx-arc');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const f = planeFrame(s.yaw, s.roll);
    const age01 = clamp01(s.age / s.life);
    // 시작할 때 살짝 커지며 퍼진다
    const R = s.length * s.scale * (0.88 + 0.12 * easeOut(clamp01(age01 * 3)));
    const W = s.width * s.scale;
    const cx = s.x + f[0] * s.ahead, cy = s.y, cz = s.z + f[2] * s.ahead;
    for (let i = 0; i <= ARC_SEG; i++) {
      const a = s.start + (s.sweep * i) / ARC_SEG;
      const ca = Math.cos(a), sa = Math.sin(a);
      const dx = f[0] * ca + f[3] * sa, dy = f[1] * ca + f[4] * sa, dz = f[2] * ca + f[5] * sa;
      const hw = 0.5 * W * TAPER[i];
      const r0 = R - hw, r1 = R + hw;
      const k = o + i * 6;
      out[k] = cx + dx * r0; out[k + 1] = cy + dy * r0; out[k + 2] = cz + dz * r0;
      out[k + 3] = cx + dx * r1; out[k + 4] = cy + dy * r1; out[k + 5] = cz + dz * r1;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.025);
    out[2] = s.seed;
    // 그려지는 진행: 수명의 앞 38%에 걸쳐 0 → 1.15
    out[3] = Math.min(1.15, (s.age / (s.life * 0.38)) * 1.15);
  }

  /** aTint2.w = UV 스크롤 속도 */
  protected override tint2w(): number { return 1.4; }
}
